import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';
import { depositPollInterval, depositSettlementToken } from '@/lib/quote/depositSettlement';
import type {
  PdfCompanyInfo,
  PdfCustomFieldValue,
} from '@/components/documents/estimates/pdfTypes';
import type { CustomerApprovalMethod, Estimate } from '@/types/estimate';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';
import type { EstimatePresentation } from '@/types/settings';
import type { PublicReviewSummary } from '@/components/public/SingleScopeReviews';
import type {
  CreateStripeSessionResponse,
  PublicPaymentConfig,
  PublicWisetackCta,
} from '@/types/public';

export interface PortalEstimateListItem {
  id: string;
  estimateNumber: string;
  title: string | null;
  status: string;
  total: number;
  /** Date-only calendar value (validUntil) — render with a date-only formatter,
   * not a tz instant, or the day shifts for customers west of UTC. */
  validUntil: string | null;
}

/** NOT-305 deposit surcharge disclosure, mirrored from the public payload. */
export interface PortalDepositSurcharge {
  enabled: boolean;
  surchargePercent: number;
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  discountCents?: number;
  surchargeAmount: number;
  totalWithSurcharge: number;
  achNoFeeOffered: boolean;
  /**
   * Rails the customer is actually offered, server-resolved from the same list
   * the checkout-session path builds. Rail availability is independent of
   * surcharging — a tenant that accepts bank transfers but charges no card fee
   * must still be offered the bank rail. Optional for back-compat with cached
   * payloads that predate the fields.
   */
  cardOffered?: boolean;
  achOffered?: boolean;
}

/**
 * The rich customer-facing estimate context. This is the SAME payload the public
 * quote page renders from (`getPublicEstimateContext` on the backend) — the
 * estimate nested under `estimate`, plus company branding, custom fields, PDF
 * visibility, deposit surcharge, service-fee note, timezone and wisetack. Sharing
 * the shape is what lets the portal reuse the public approve + signed-PDF flow.
 */
export interface PortalEstimateDetail {
  estimate: Estimate;
  staleItems?: unknown[];
  activePdf?: { url: string; reason?: string | null } | null;
  company?: PdfCompanyInfo | null;
  customFields?: PdfCustomFieldValue[];
  estimateSettings?: { presentation?: EstimatePresentation };
  depositCollectedSummary?: Estimate['depositCollectedSummary'];
  depositSurcharge?: PortalDepositSurcharge;
  serviceFee?: { show: boolean; percent: number; chargedAmount?: number | null };
  timezone?: string | null;
  wisetack?: PublicWisetackCta | null;
  /** Customer-facing attribution — name only (id stays stripped server-side). */
  presentedBy?: { name: string } | null;
  /** Aggregate social proof from synced external reviews; null when none. */
  reviews?: PublicReviewSummary | null;
  /** Resolved document-template layout — when present, the PDF renders through it. */
  resolvedDocLayout?: EstimateDocLayout | null;
  /**
   * Which pay UI the deposit CTA must render: Stripe hosted Checkout, the
   * Payabli embedded component, or nothing. Resolved from the session tenant.
   * The portal used to assume Stripe, so a Payabli tenant's customer got a CTA
   * that 400s and no way to pay a required deposit.
   */
  payment?: PublicPaymentConfig;
  /**
   * The newest inline (Payabli) deposit attempt, for an uncollected deposit
   * only. SERVER-derived, so a reload during an unsettled bank transfer resumes
   * the "submitted" state instead of re-offering the CTA over money in flight.
   */
  depositAttempt?: { status: string } | null;
  /**
   * The contact the deposit charge will be billed to — the SESSION contact,
   * resolved server-side. Not `estimate.contactId`: the portal also admits a
   * BILL-TO contact, and the Payabli embedded component must mount with the same
   * `customerNumber` the server-side exchange binds, or Payabli rejects the
   * payer and a bill-to customer can approve but never pay.
   */
  payerContactId?: string | null;
}

/** The approve request body (the customer-approve DTO, minus server-derived fields). */
export interface PortalApproveEstimateBody {
  acceptMembershipOffer?: boolean;
  membershipBillingOptionId?: string;
  method?: CustomerApprovalMethod;
  customerSignatureName?: string;
  customerSignatureText?: string;
  customerSignatureImage?: string;
  consentGranted?: boolean;
  consentUserAgent?: string;
  lineItems: { lineItemId: string; lineKey?: string; isSelected: boolean }[];
}

export interface PortalApproveEstimateResponse {
  depositCollectedSummary?: Estimate['depositCollectedSummary'];
  estimate: Partial<Estimate>;
  depositSurcharge: PortalDepositSurcharge;
}

const enc = encodeURIComponent;

/** The signed-in contact's estimates (newest first). */
export function usePortalEstimates() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'estimates', token],
    queryFn: () => portalGet<PortalEstimateListItem[]>('/api/portal/estimates'),
    enabled: Boolean(token),
    retry: false,
  });
}

/**
 * One estimate the contact owns; 404s (via ApiError) for anything else. When the
 * customer lands back from a deposit checkout (`deposit=success`), the query keys
 * on a settlement token and polls until the deposit registers — identical to the
 * public quote page (see lib/quote/depositSettlement).
 */
export function usePortalEstimate(id: string, deposit?: 'success' | 'cancel') {
  const token = usePortalAuthStore((s) => s.token);
  const settlementToken = depositSettlementToken(id, deposit);
  return useQuery({
    queryKey: ['portal', 'estimate', id, token, settlementToken],
    queryFn: () => portalGet<PortalEstimateDetail>(`/api/portal/estimates/${enc(id)}`),
    enabled: Boolean(token && id),
    retry: false,
    refetchInterval: (query) =>
      depositPollInterval(
        deposit,
        (query.state.data as PortalEstimateDetail | undefined)?.estimate?.depositCollected,
        (query.state.data as PortalEstimateDetail | undefined)?.estimate?.depositClearing,
      ),
    // `undefined` keeps the client default (TanStack's `true`); `false` opted
    // every normal estimate visit out of focus refetching, so a customer who
    // tabbed back never saw the business revise or re-send the estimate.
    // After a deposit checkout, `'always'` rather than `true`: the client's
    // `staleTime: 60_000` would skip a plain `true` for anyone tabbing back
    // within the first minute. Same rule as usePortalInvoice.
    refetchOnWindowFocus: deposit === 'success' ? 'always' : undefined,
  });
}

function invalidateEstimate(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  void queryClient.invalidateQueries({ queryKey: ['portal', 'estimate', id] });
  void queryClient.invalidateQueries({ queryKey: ['portal', 'estimates'] });
}

/** Decline the estimate (→ DECLINED). */
export function usePortalRejectEstimate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      portalPost<PortalEstimateDetail>(`/api/portal/estimates/${enc(id)}/reject`, {
        reason,
      }),
    onSuccess: () => invalidateEstimate(queryClient, id),
  });
}

/**
 * Approve the estimate (→ APPROVED). Delegates to the portal approve endpoint,
 * which proves ownership and runs the canonical `customerApprove`. On success the
 * estimate cache is invalidated so the deposit CTA renders against the fresh
 * (post-approval) status + amount.
 */
export function usePortalApproveEstimate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PortalApproveEstimateBody) =>
      portalPost<PortalApproveEstimateResponse>(`/api/portal/estimates/${enc(id)}/approve`, body),
    onSuccess: (data) => {
      // Seed the detail cache from the authoritative approve response BEFORE
      // invalidating — the repriced estimate + recomputed surcharge are already
      // in hand, so the deposit CTA renders immediately even if the follow-up
      // refetch lags or fails (a failed refetch resolves silently in TanStack and
      // would otherwise leave the pre-approval snapshot, hiding the CTA). Mirrors
      // the public quote's seedApprovedPublicEstimate. Prefix key matches every
      // detail query (token + settlement segments included).
      queryClient.setQueriesData<PortalEstimateDetail>(
        { queryKey: ['portal', 'estimate', id] },
        (prev) =>
          prev
            ? {
                ...prev,
                estimate: { ...prev.estimate, ...data.estimate },
                depositSurcharge: data.depositSurcharge ?? prev.depositSurcharge,
                depositCollectedSummary:
                  data.depositCollectedSummary === undefined
                    ? prev.depositCollectedSummary
                    : data.depositCollectedSummary,
              }
            : prev,
      );
      invalidateEstimate(queryClient, id);
    },
  });
}

/**
 * Open a Stripe Checkout session for the approved estimate's deposit. INITIATES
 * the checkout only — the customer completes the charge on Stripe's hosted page.
 */
export function usePortalCreateDepositSession(id: string) {
  return useMutation({
    mutationFn: (input: {
      method?: 'card' | 'ach';
      /** The add-ons ticked, when a REQUIRED deposit is paid before approval. */
      lineItems?: { lineItemId: string; lineKey?: string; isSelected: boolean }[];
    }) =>
      portalPost<CreateStripeSessionResponse>(
        `/api/portal/estimates/${enc(id)}/stripe-deposit-session`,
        {
          ...(input.method ? { method: input.method } : {}),
          ...(input.lineItems ? { lineItems: input.lineItems } : {}),
        },
      ),
  });
}

/** Fast-path confirm of a paid Checkout session on the customer's return. */
export function portalConfirmDeposit(id: string, sessionId: string) {
  return portalPost<{ depositCollected: boolean }>(
    `/api/portal/estimates/${enc(id)}/stripe-deposit-confirm`,
    { sessionId },
  );
}

/**
 * Pay the deposit with Payabli: the embedded component hands back a one-time
 * token and the server charges the estimate's OWN required deposit — the client
 * never sends an amount. `surchargeDisclosed` attests the page rendered the card
 * service-fee note; the backend charges no fee without it.
 *
 * Unlike the Stripe pair above, this MOVES MONEY on call.
 */
export function portalChargeDepositPayabli(
  id: string,
  temporaryToken: string,
  surchargeDisclosed: boolean,
  /** The add-ons ticked, when a REQUIRED deposit is paid before approval. */
  selection?: { lineItems: { lineItemId: string; lineKey?: string; isSelected: boolean }[] },
) {
  return portalPost<{
    status: string;
    amount: number;
    surchargeAmount: number;
    depositCollected: boolean;
  }>(`/api/portal/estimates/${enc(id)}/payabli-deposit`, {
    temporaryToken,
    ...(surchargeDisclosed ? { surchargeDisclosed: true } : {}),
    ...(selection ? { lineItems: selection.lineItems } : {}),
  });
}
